import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Building2, TrendingUp, Heart, Users, Trophy, ArrowRight, Euro, Wallet, PieChart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CorporateFund = () => {
  const { isAuthenticated, user, token } = useAuth();
  const { language } = useLanguage();
  const [fundData, setFundData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [donationAmount, setDonationAmount] = useState('');
  const [donationCurrency, setDonationCurrency] = useState('EUR');
  const [donating, setDonating] = useState(false);

  useEffect(() => {
    fetchFundData();
    fetchLeaderboard();
  }, []);

  const fetchFundData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/corporate-fund`);
      setFundData(response.data);
    } catch (error) {
      console.error('Error fetching fund data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/corporate-fund/leaderboard`);
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  const handleContribute = async () => {
    if (!donationAmount || parseFloat(donationAmount) <= 0) {
      toast.error(language === 'it' ? 'Inserisci un importo valido' : 'Enter a valid amount');
      return;
    }

    setDonating(true);
    try {
      await axios.post(
        `${API_URL}/api/corporate-fund/contribute`,
        {
          amount: parseFloat(donationAmount),
          currency: donationCurrency,
          payment_method: 'bank_transfer'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(language === 'it' ? 'Contribuzione registrata! Ti invieremo le coordinate bancarie.' : 'Contribution registered! We will send you the bank details.');
      setShowDonateModal(false);
      setDonationAmount('');
      fetchFundData();
      fetchLeaderboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'it' ? 'Errore' : 'Error'));
    } finally {
      setDonating(false);
    }
  };

  const isCompany = user?.user_type === 'company';

  return (
    <div className="min-h-screen bg-stone-100 overflow-y-auto">
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-emerald-800 to-emerald-900 rounded-3xl p-8 md:p-12 mb-8 text-white relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-700/30 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-emerald-700/20 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative z-10 max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <Building2 className="h-8 w-8" />
              <span className="text-emerald-200 font-medium">
                {language === 'it' ? 'Fondo Aziendale Moneyfor' : 'Moneyfor Corporate Fund'}
              </span>
            </div>
            
            <h1 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              {language === 'it' 
                ? 'Le aziende che cambiano il mondo' 
                : 'Companies changing the world'}
            </h1>
            
            <p className="text-emerald-100 text-lg mb-8">
              {language === 'it'
                ? 'Un fondo comune gestito da Moneyfor dove le aziende contribuiscono per aiutare chi ha più bisogno. Trasparenza, impatto, responsabilità sociale.'
                : 'A common fund managed by Moneyfor where companies contribute to help those most in need. Transparency, impact, social responsibility.'}
            </p>

            {isAuthenticated && isCompany ? (
              <Dialog open={showDonateModal} onOpenChange={setShowDonateModal}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="contribute-fund-btn"
                    className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg"
                  >
                    <Heart className="h-5 w-5 mr-2" fill="currentColor" />
                    {language === 'it' ? 'Contribuisci al Fondo' : 'Contribute to Fund'}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl">
                      {language === 'it' ? 'Contribuisci al Fondo Aziendale' : 'Contribute to Corporate Fund'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>{language === 'it' ? 'Importo' : 'Amount'}</Label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
                          <Input
                            data-testid="fund-amount-input"
                            type="number"
                            min="100"
                            step="100"
                            placeholder="1000"
                            value={donationAmount}
                            onChange={(e) => setDonationAmount(e.target.value)}
                            className="pl-10 h-12 rounded-xl"
                          />
                        </div>
                        <Select value={donationCurrency} onValueChange={setDonationCurrency}>
                          <SelectTrigger className="w-24 h-12 rounded-xl">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="GBP">GBP</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="bg-emerald-50 rounded-xl p-4">
                      <p className="text-sm text-emerald-800">
                        {language === 'it'
                          ? 'Dopo la conferma, riceverai le coordinate bancarie per effettuare il bonifico. La donazione sarà visibile nel fondo dopo la conferma del pagamento.'
                          : 'After confirmation, you will receive bank details to make the transfer. The donation will be visible in the fund after payment confirmation.'}
                      </p>
                    </div>

                    <Button
                      data-testid="confirm-contribution-btn"
                      onClick={handleContribute}
                      disabled={donating}
                      className="w-full h-12 bg-emerald-800 hover:bg-emerald-900 text-white rounded-xl font-semibold"
                    >
                      {donating ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      ) : (
                        language === 'it' ? 'Conferma Contribuzione' : 'Confirm Contribution'
                      )}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            ) : !isAuthenticated ? (
              <Link to="/register">
                <Button className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg">
                  {language === 'it' ? 'Registra la tua azienda' : 'Register your company'}
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </Link>
            ) : (
              <p className="text-emerald-200">
                {language === 'it' 
                  ? 'Solo le aziende registrate possono contribuire al fondo.' 
                  : 'Only registered companies can contribute to the fund.'}
              </p>
            )}
          </div>
        </motion.div>

        {/* Stats */}
        {!loading && fundData && (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Wallet className="h-5 w-5 text-emerald-800" />
                </div>
                <span className="text-stone-600">
                  {language === 'it' ? 'Saldo Disponibile' : 'Available Balance'}
                </span>
              </div>
              <p className="font-heading text-3xl font-bold text-emerald-800">
                €{(fundData.available_balance || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-amber-700" />
                </div>
                <span className="text-stone-600">
                  {language === 'it' ? 'Totale Raccolto' : 'Total Raised'}
                </span>
              </div>
              <p className="font-heading text-3xl font-bold text-amber-700">
                €{(fundData.total_balance || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-indigo-700" />
                </div>
                <span className="text-stone-600">
                  {language === 'it' ? 'Aziende Contributrici' : 'Contributing Companies'}
                </span>
              </div>
              <p className="font-heading text-3xl font-bold text-indigo-700">
                {fundData.total_contributions || 0}
              </p>
            </motion.div>
          </div>
        )}

        {/* How it works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8 mb-8"
        >
          <h2 className="font-heading text-2xl font-bold text-stone-900 mb-6">
            {language === 'it' ? 'Come funziona il Fondo' : 'How the Fund works'}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <span className="font-heading text-xl font-bold text-emerald-800">1</span>
              </div>
              <h3 className="font-semibold text-stone-900 mb-2">
                {language === 'it' ? 'Contribuisci' : 'Contribute'}
              </h3>
              <p className="text-stone-600 text-sm">
                {language === 'it'
                  ? 'Le aziende versano nel fondo comune tramite bonifico bancario'
                  : 'Companies contribute to the common fund via bank transfer'}
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <span className="font-heading text-xl font-bold text-amber-700">2</span>
              </div>
              <h3 className="font-semibold text-stone-900 mb-2">
                {language === 'it' ? 'Gestiamo' : 'We Manage'}
              </h3>
              <p className="text-stone-600 text-sm">
                {language === 'it'
                  ? 'Moneyfor gestisce il fondo e seleziona le richieste più urgenti'
                  : 'Moneyfor manages the fund and selects the most urgent requests'}
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
                <span className="font-heading text-xl font-bold text-indigo-700">3</span>
              </div>
              <h3 className="font-semibold text-stone-900 mb-2">
                {language === 'it' ? 'Distribuiamo' : 'We Distribute'}
              </h3>
              <p className="text-stone-600 text-sm">
                {language === 'it'
                  ? 'I fondi vengono distribuiti a chi ne ha più bisogno con massima trasparenza'
                  : 'Funds are distributed to those most in need with full transparency'}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Corporate Leaderboard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <Trophy className="h-6 w-6 text-amber-500" />
            <h2 className="font-heading text-2xl font-bold text-stone-900">
              {language === 'it' ? 'Top Aziende Contributrici' : 'Top Contributing Companies'}
            </h2>
          </div>

          {leaderboard.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-16 w-16 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">
                {language === 'it' ? 'Sii la prima azienda a contribuire!' : 'Be the first company to contribute!'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {leaderboard.map((company, index) => (
                <div
                  key={company.company_id}
                  className={`flex items-center gap-4 p-4 rounded-xl ${
                    index === 0 ? 'bg-amber-50 border border-amber-200' : 'bg-stone-50'
                  }`}
                >
                  <div className="w-10 text-center">
                    {index === 0 ? (
                      <Trophy className="h-6 w-6 text-amber-500 mx-auto" />
                    ) : (
                      <span className="font-heading text-xl font-bold text-stone-400">{company.rank}</span>
                    )}
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-emerald-800" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-stone-900">{company.company_name}</p>
                    <p className="text-sm text-stone-500">
                      {company.contributions_count} {language === 'it' ? 'contribuzioni' : 'contributions'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-xl font-bold text-emerald-800">
                      €{company.total_donated.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </main>
      <Footer />
    </div>
  );
};

export default CorporateFund;
